/**
 * ChallengeCardHeader
 * Supports two layouts: default (name + badges) and home (avatar left, name/subtitle center, status/CTA right).
 * Uses FriendAvatar for opponent avatar (same as friends tab: avatarId > fallbackUrl > initials).
 */

import React, { type ReactNode, useRef, useEffect } from 'react';
import { View, Text, useWindowDimensions, Animated, Easing, type ImageSourcePropType, type ViewStyle } from 'react-native';
import { Image } from 'expo-image';
import { IDLE_COIN_SOURCE } from '../../constants/assets'
import { Zap, ChevronDown } from 'lucide-react-native';
import { ZportiBadge } from '../ui/zportiBadge';
import { FriendAvatar } from '../friends/shared/FriendAvatar';
import { AVATAR_IDS, type AvatarId } from '../../constants/avatarIds';
import type { DisplayAvatarId } from '../../src/avatars/types/avatar.types';
import type { EquippedMap } from '../avatars/LayeredAvatarLab/characterCustomizer.types';
import { MONKEY_AVATAR_ID } from '../../constants/monkey';
import { useCountdownToDate } from '../../src/challenges/hooks/useCountdownToDate';
import type { ChallengeWithStats } from '../../src/challenges/types/challengeCard.types';
import type { SectionBucket } from '../../src/challenges/types/challenges.types';
import {
  challengeCardStyles as defaultStyles,
  type ChallengeCardStyles,
} from '../../src/challenges/styles/challengeCard.styles';
import { getTheme } from '../../src/theme/getTheme';
import {
  SECTION_BUCKET_SUBTITLES,
  FINISHED_SUBTITLES,
  FINISHED_GROUP_POSITION_SUBTITLE,
  CHALLENGE_HEADER_LABELS,
  CHALLENGE_CARD_HEADER_TEST_ID,
  COUNTDOWN_STARTED,
  COUNTDOWN_SEGMENT_LABELS,
  CHALLENGE_STARTED_SUBTITLE,
} from '../../src/challenges/constants/challenges.constants';
import type { Theme } from '../../src/theme/getTheme';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../src/shared/helpers/imageWebAlt.helper';

const numFmt = new Intl.NumberFormat();
const VALID_AVATAR_IDS = new Set<string>(Object.values(AVATAR_IDS));

function isValidAvatarId(id: string | undefined): id is AvatarId {
  return typeof id === 'string' && VALID_AVATAR_IDS.has(id);
}

/** AvatarId for display (user-selectable or Monkey) */
function isDisplayAvatarId(id: string | undefined): boolean {
  return id === MONKEY_AVATAR_ID || isValidAvatarId(id);
}

const HOME_AVATAR_SIZE = 36;

interface ChallengeCardHeaderProps {
  challenge: ChallengeWithStats;
  currentUserId: string;
  displayName: string;
  /** When challenge is for a group, use this image (group default) instead of opponent avatar. */
  groupImageSource?: ImageSourcePropType;
  /** Avatar URL of the other participant (e.g. auth profile image). */
  otherParticipantAvatar?: string;
  /** Preset avatar ID of the other participant (e.g. 'blue-with-hair'). */
  otherParticipantAvatarId?: string;
  /** Custom layered look of the other participant; overrides avatarId when present. */
  otherParticipantAvatarConfig?: EquippedMap | null;
  isExpanded: boolean;
  /** Home mode: avatar + smart subtitle + CTA by bucket */
  variant?: 'home' | 'default';
  sectionBucket?: SectionBucket;
  /** ISO date of the first game in the challenge (Your Turn and Active countdown until first game). */
  firstGameDate?: string | null;
  onCancelPress?: () => void;
  /** Theme-aware styles from parent. When omitted (e.g. Storybook), light theme styles are used. */
  styles?: ChallengeCardStyles;
  /** Theme for icon colors (from parent). */
  theme?: Theme;
  /** Test ID for testing */
  testID?: string;
  stakeCoins?: number;
  participantCount?: number;
  /** When any fixture is in play but the list bucket is not `live`, still show the LIVE header badge */
  showLiveMatchBadge?: boolean;
}

function getSubtitleForBucket(
  bucket: SectionBucket | undefined,
  displayName: string,
  finishedOutcome?: 'won' | 'lost' | 'draw',
  groupId?: string,
  myRank?: number | null
): string {
  if (bucket == null) return SECTION_BUCKET_SUBTITLES.default;
  if (bucket === 'waitingForFriends') return `${SECTION_BUCKET_SUBTITLES.waitingForFriends} ${displayName}`;
  if (bucket === 'finished') {
    if (groupId && myRank != null && myRank > 0) {
      return FINISHED_GROUP_POSITION_SUBTITLE.replace('%d', String(myRank));
    }
    if (finishedOutcome != null) {
      if (finishedOutcome === 'won') return FINISHED_SUBTITLES.WON;
      if (finishedOutcome === 'draw') return FINISHED_SUBTITLES.DRAW;
      return FINISHED_SUBTITLES.LOST;
    }
  }
  return SECTION_BUCKET_SUBTITLES[bucket];
}

function getCompactHeaderBadges(challenge: ChallengeWithStats, isCreator: boolean): ReactNode {
  if (!isCreator) return <ZportiBadge variant="info" text={challenge.status} />;
  const participantStatuses = challenge.participantStatuses;
  if (!participantStatuses?.length) {
    return (
      <>
        <ZportiBadge variant="info" text={challenge.status} />
        <ZportiBadge variant="success" text={CHALLENGE_HEADER_LABELS.CREATOR} />
      </>
    );
  }
  const otherParticipants = participantStatuses.filter((p) => p.userId !== challenge.creatorId);
  if (otherParticipants.length === 0) {
    return (
      <>
        <ZportiBadge variant="info" text={challenge.status} />
        <ZportiBadge variant="success" text={CHALLENGE_HEADER_LABELS.CREATOR} />
      </>
    );
  }
  const hasDeclined = otherParticipants.some((p) => p.status === 'DECLINED');
  if (hasDeclined) {
    return (
      <>
        <ZportiBadge variant="error" text={CHALLENGE_HEADER_LABELS.DECLINED} />
        <ZportiBadge variant="success" text={CHALLENGE_HEADER_LABELS.CREATOR} />
      </>
    );
  }
  const hasPending = otherParticipants.some((p) => p.status === 'PENDING');
  if (hasPending) {
    return (
      <>
        <ZportiBadge variant="warning" text={CHALLENGE_HEADER_LABELS.PENDING} />
        <ZportiBadge variant="success" text={CHALLENGE_HEADER_LABELS.CREATOR} />
      </>
    );
  }
  return (
    <>
      <ZportiBadge variant="info" text={challenge.status} />
      <ZportiBadge variant="success" text={CHALLENGE_HEADER_LABELS.CREATOR} />
    </>
  );
}

export const ChallengeCardHeader: React.FC<ChallengeCardHeaderProps> = (props) => {
  const {
    challenge,
    currentUserId,
    displayName,
    otherParticipantAvatar,
    otherParticipantAvatarId,
    otherParticipantAvatarConfig,
    isExpanded,
    variant = 'default',
    sectionBucket,
    firstGameDate,
    testID,
    stakeCoins,
    participantCount,
    showLiveMatchBadge = false,
  } = props;
  const groupImageSource = 'groupImageSource' in props ? props.groupImageSource : undefined;
  const styles = props.styles ?? defaultStyles;
  const theme = props.theme ?? getTheme('light');
  const white = theme?.white ?? '#FFFFFF';
  const isCreator = challenge.creatorId === currentUserId;
  const useHomeLayout = variant === 'home' || sectionBucket != null;
  const bucket = sectionBucket;
  const finishedOutcome =
    bucket === 'finished'
      ? challenge.leader != null
        ? challenge.leader.userId === currentUserId
          ? ('won' as const)
          : Number(challenge.myPoints) === Number(challenge.leader.points)
            ? ('draw' as const)
            : ('lost' as const)
        : ('draw' as const) // no single leader (e.g. tie or all 0) → show "Challenge draw"
      : undefined;
  const isYourMove = bucket === 'yourMove';
  const isActive = bucket === 'active';
  const isWaitingForFriends = bucket === 'waitingForFriends';
  const countdownTarget =
    isYourMove || isActive || isWaitingForFriends ? (firstGameDate ?? null) : null;
  const { display: countdownDisplay, isUrgent, segments } = useCountdownToDate(
    countdownTarget
  );
  const showCountdownYourTurn = isYourMove && countdownDisplay !== '';
  const showCountdownActive = isActive && countdownDisplay !== '';
  const showCountdownWaiting = isWaitingForFriends && countdownDisplay !== '';
  const showCountdown = showCountdownYourTurn || showCountdownActive || showCountdownWaiting;
  const baseSubtitle = useHomeLayout
    ? getSubtitleForBucket(bucket, displayName, finishedOutcome, challenge.groupId, challenge.myRank)
    : null;
  const headerSubtitle =
    useHomeLayout && (isYourMove || isActive || isWaitingForFriends) && countdownDisplay === COUNTDOWN_STARTED
      ? CHALLENGE_STARTED_SUBTITLE
      : baseSubtitle;
  const { width: windowWidth } = useWindowDimensions();
  const isSmallScreen = windowWidth < 370;
  const isMediumScreen = windowWidth < 430;
  const isNarrowScreen = windowWidth < 400;

  const chevronAnim = useRef(new Animated.Value(isExpanded ? 1 : 0)).current;
  useEffect(() => {
    Animated.timing(chevronAnim, {
      toValue: isExpanded ? 1 : 0,
      duration: 220,
      easing: Easing.inOut(Easing.ease),
      useNativeDriver: true,
    }).start();
  }, [isExpanded, chevronAnim]);

  const coinOutcomeChip = (() => {
    if (stakeCoins == null || bucket !== 'finished') return null;
    const count = Math.max(participantCount ?? 2, 2);
    const isZeroStake = stakeCoins === 0;
    const isWin = !isZeroStake && finishedOutcome === 'won';
    const isLoss = !isZeroStake && finishedOutcome === 'lost';
    const coinLabel = isZeroStake
      ? '0'
      : isWin
        ? `+${numFmt.format(stakeCoins * (count - 1))}`
        : isLoss
          ? `-${numFmt.format(stakeCoins)}`
          : numFmt.format(stakeCoins);
    const chipVariant = isWin ? styles.homeCoinOutcomeChipWin : isLoss ? styles.homeCoinOutcomeChipLoss : styles.homeCoinOutcomeChipDraw;
    const textVariant = isWin ? styles.homeCoinOutcomeTextWin : isLoss ? styles.homeCoinOutcomeTextLoss : styles.homeCoinOutcomeTextDraw;
    return (
      <View style={[styles.homeCoinOutcomeChip, chipVariant]}>
        <Text style={[styles.homeCoinOutcomeText, textVariant]}>{coinLabel}</Text>
        <Image
          source={IDLE_COIN_SOURCE}
          style={styles.homeCoinIcon}
          alt={WEB_IMAGE_ALT_DECORATIVE}
        />
      </View>
    );
  })();

  if (useHomeLayout) {
    return (
      <View style={styles.homeHeaderRow} testID={testID ?? CHALLENGE_CARD_HEADER_TEST_ID}>
        {/* Single row: Avatar + Name left | Actions (Pot + Chevron pill) right, tight spacing */}
          <View style={styles.homeHeaderTopRow}>
          <View style={styles.homeHeaderTopLeft}>
            <View style={styles.homeHeaderAvatar}>
              <FriendAvatar
                {...(groupImageSource != null ? { imageSource: groupImageSource } : {})}
                avatarId={isDisplayAvatarId(otherParticipantAvatarId) ? (otherParticipantAvatarId as DisplayAvatarId) : undefined}
                avatarConfig={otherParticipantAvatarConfig}
                fallbackUrl={otherParticipantAvatar ?? undefined}
                size={HOME_AVATAR_SIZE}
                nameForInitials={displayName || challenge.name}
              />
            </View>
            <View style={styles.homeHeaderCenter}>
              <Text style={[styles.homeHeaderName, isMediumScreen && styles.homeHeaderNameMedium, isSmallScreen && styles.homeHeaderNameSmall]} numberOfLines={1}>
                {displayName || challenge.name}
              </Text>
              {stakeCoins != null && bucket !== 'finished' && (() => {
                const count = Math.max(participantCount ?? 1, 1);
                const isEntry = bucket === 'yourMove' || bucket === 'waitingForFriends';
                const coinAmount = isEntry ? stakeCoins : stakeCoins * count;
                const coinLabel = isEntry ? 'entry' : 'POT';
                const s = styles as Record<string, object>;
                return (
                  <View style={s.homeHeaderPotRow} key="pot-below-name">
                    <Text style={s.homeHeaderPotLabel}>{coinLabel}</Text>
                    <Image
                      source={IDLE_COIN_SOURCE}
                      style={styles.homeCoinIcon}
                      alt={WEB_IMAGE_ALT_DECORATIVE}
                    />
                    <Text style={s.homeHeaderPotValue}>{numFmt.format(coinAmount)}</Text>
                  </View>
                );
              })()}
              {stakeCoins == null && headerSubtitle ? (
                <Text style={[styles.homeHeaderSubtitle, isMediumScreen && styles.homeHeaderSubtitleMedium, isSmallScreen && styles.homeHeaderSubtitleSmall]} numberOfLines={1}>
                  {headerSubtitle}
                </Text>
              ) : null}
              {stakeCoins != null && bucket !== 'finished' && !showCountdown && headerSubtitle ? (
                <Text style={[styles.homeHeaderSubtitle, isMediumScreen && styles.homeHeaderSubtitleMedium, isSmallScreen && styles.homeHeaderSubtitleSmall]} numberOfLines={1}>
                  {headerSubtitle}
                </Text>
              ) : null}
            </View>
          </View>
          <View style={styles.homeHeaderTopRight}>
            <View style={(styles as ChallengeCardStyles & { homeActionsBlock: ViewStyle }).homeActionsBlock}>
              {(bucket === 'live' || (showLiveMatchBadge && bucket !== 'finished')) && (
                <View style={[styles.homeBadgeWithIcon, styles.homeBadgeError]}>
                  <Zap size={12} color={white} />
                  <Text style={styles.homeBadgeText}>{CHALLENGE_HEADER_LABELS.LIVE}</Text>
                </View>
              )}
{stakeCoins != null && bucket === 'finished' && coinOutcomeChip}
              {showCountdown &&
                (segments ? (
                  <View
                    style={[
                      (styles as Record<string, object>).homeTimerInline,
                      isUrgent && (styles as Record<string, object>).homeTimerInlineUrgent,
                    ]}
                  >
                    <View style={styles.homeTimerSegment}>
                      <Text style={[styles.homeTimerNumber, isUrgent && styles.homeTimerNumberUrgent]}>
                        {segments.days}
                      </Text>
                      <Text style={styles.homeTimerLabel}>{COUNTDOWN_SEGMENT_LABELS.days}</Text>
                    </View>
                    <Text style={styles.homeTimerColon}>:</Text>
                    <View style={styles.homeTimerSegment}>
                      <Text style={[styles.homeTimerNumber, isUrgent && styles.homeTimerNumberUrgent]}>
                        {segments.hours}
                      </Text>
                      <Text style={styles.homeTimerLabel}>{COUNTDOWN_SEGMENT_LABELS.hours}</Text>
                    </View>
                    <Text style={styles.homeTimerColon}>:</Text>
                    <View style={styles.homeTimerSegment}>
                      <Text style={[styles.homeTimerNumber, isUrgent && styles.homeTimerNumberUrgent]}>
                        {segments.minutes}
                      </Text>
                      <Text style={styles.homeTimerLabel}>{COUNTDOWN_SEGMENT_LABELS.minutes}</Text>
                    </View>
                  </View>
                ) : (
                  <View style={(styles as Record<string, object>).homeTimerInline}>
                    <Text style={styles.homeTimerStartedText}>{COUNTDOWN_STARTED}</Text>
                  </View>
                ))}
              <View style={(styles as ChallengeCardStyles & { homeChevronPill: ViewStyle }).homeChevronPill}>
                <Animated.View style={[
                  styles.homeHeaderChevronWrap,
                  { transform: [{ rotate: chevronAnim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '180deg'] }) }] },
                ]}>
                  <ChevronDown size={isNarrowScreen ? 12 : 14} color={theme.gray?.[500] ?? '#6B7280'} />
                </Animated.View>
              </View>
            </View>
          </View>
        </View>
      </View>
    );
  }

  // Default layout: name + hint left, badges right
  return (
    <View style={styles.compactHeader} testID={testID ?? CHALLENGE_CARD_HEADER_TEST_ID}>
      <View style={styles.compactHeaderLeft}>
        <Text style={styles.compactLeague}>
          {displayName || challenge.name}
        </Text>
        {!isExpanded && (
          <Text style={styles.compactCollapsedHint}>
            {SECTION_BUCKET_SUBTITLES.default}
          </Text>
        )}
      </View>
      <View style={styles.compactHeaderRight}>
        {getCompactHeaderBadges(challenge, isCreator)}
      </View>
    </View>
  );
};
